import { checkLearningAccess } from './utils/learningAccess';
import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Amplify } from 'aws-amplify';
import Breadcrumbs from './components/Breadcrumbs';
import TopNavBar from './components/TopNavBar';
import { DataStore } from '@aws-amplify/datastore';
import { getCurrentUser } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { Progress } from './models';
import ProgressTracker from './ProgressTracker';
import Sidebar from './sidebar';
import AuthPage from './AuthPage';
import Questionnaire from './Questionnaire';
import DevExQuestionnaire from './DevExQuestionnaire';
import CloudPlatformEngineerQuestionnaire from './CloudPlatformEngineerQuestionnaire';
import DevOpsQuestionnaire from './DevOpsQuestionnaire';
import SREQuestionnaire from './SREQuestionnaire';
import PenetrationTestingQuestionnaire from './PenetrationTestingQuestionnaire';
import CyberGRCQuestionnaire from './CyberGRCQuestionnaire';
import ProductManagerQuestionnaire from './ProductManagerQuestionnaire';
import AIDataEngineerQuestionnaire from './AIDataEngineerQuestionnaire';
import SecurityAnalystQuestionnaire from './SecurityAnalystQuestionnaire';
import FullStackEngineerQuestionnaire from './FullStackEngineerQuestionnaire';
import DevSecAIQuestionnaire from './DevSecAIQuestionnaire';
import LandingPage from './LandingPage';
import RolesPage from './RolesPage';
import TermsOfService from './TermsOfService';
import PrivacyPolicy from './PrivacyPolicy';
import SEO from './components/SEO';
import { getFeatureFlag } from './featureFlags';
import awsconfig from './aws-exports';
import PortalPage from './PortalPage';
import PricingPage from './PricingPage';
import UserProfile from './components/UserProfile';
import CookieConsent from './components/CookieConsent';
import Footer from './components/Footer';
import CertificationRevision from './components/CertificationRevision';
import CVGenerator from './components/CVGenerator';
import AdminPortal from './pages/AdminPortal';
import { AdminRoute } from './utils/isAdmin';
import CheckoutSuccess from './pages/CheckoutSuccess';
import NotFound from './pages/NotFound';
import CampaignAttribution from './components/CampaignAttribution';

Amplify.configure(awsconfig);

function App() {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [formData, setFormData] = useState({});
    const [roadmap, setRoadmap] = useState(null);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [progress, setProgress] = useState({});
    const [userId, setUserId] = useState(null);

    const defaultRoadmap = {
        items: [
            { id: 'item-1', title: 'Learn DevOps Basics', duration: 4, month: 1, description: 'Introduction to DevOps principles and practices', type: 'skill' },
            { id: 'item-2', title: 'Cloud Fundamentals', duration: 4, month: 2, description: 'Learn basics of cloud computing', type: 'skill' },
            { id: 'item-3', title: 'Security Fundamentals', duration: 4, month: 3, description: 'Introduction to cybersecurity concepts', type: 'skill' },
            { id: 'cert-1', name: 'AWS Certified Cloud Practitioner', duration: 6, month: 4, cost: 100, type: 'certification' },
            { id: 'item-4', title: 'CI/CD Pipelines', duration: 4, month: 5, description: 'Learn to build and manage CI/CD pipelines', type: 'skill' },
            { id: 'item-5', title: 'Infrastructure as Code', duration: 4, month: 6, description: 'Introduction to IaC tools like Terraform', type: 'skill' },
        ]
    };
    
    const fetchProgress = useCallback(async () => {
        try {
            // Wait a bit to ensure DataStore is ready
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Retry logic to handle DataStore clearing state
            let retries = 0;
            while (retries < 5) {
                try {
                    // Progress is owner-scoped by @auth, so querying without predicates returns only this user's records.
                    const savedProgress = (await DataStore.query(Progress)).filter(p => !p.id.includes('::project::'));
                    if (savedProgress.length > 0 && savedProgress[0]?.data) {
                        setProgress(typeof savedProgress[0].data === 'string' ? JSON.parse(savedProgress[0].data) : savedProgress[0].data);
                    }
                    break; // Success
                } catch (error) {
                    if (error.message && error.message.includes('Clearing') && retries < 4) {
                        await new Promise(resolve => setTimeout(resolve, 1000));
                        retries++;
                        continue;
                    }
                    throw error;
                }
            }
        } catch (error) {
            console.error('Error fetching progress:', error);
        }
    }, []);

    const fetchSubscriptionStatus = useCallback(async () => {
        try {
            // Wait a bit to ensure DataStore is ready
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Retry logic to handle DataStore clearing state
            let retries = 0;
            while (retries < 5) {
                try {
                    // Subscription plan is owner-scoped; query without predicates for current user.

                    setIsSubscribed(await checkLearningAccess('any'));
                    break; // Success
                } catch (error) {
                    if (error.message && error.message.includes('Clearing') && retries < 4) {
                        await new Promise(resolve => setTimeout(resolve, 1000));
                        retries++;
                        continue;
                    }
                    throw error;
                }
            }
        } catch (error) {
            console.error('Error fetching subscription status:', error);
        }
    }, [userId]);

    useEffect(() => {
        checkAuthState();

        const unsubscribe = Hub.listen('auth', ({ payload: { event, data } }) => {
            switch (event) {
                case 'signedIn':
                case 'signIn':
                    setIsAuthenticated(true);
                    setUserId(data?.userId || data?.username || null);
                    break;
                case 'signedOut':
                case 'signOut':
                    setIsAuthenticated(false);
                    setUserId(null);
                    setProgress({});
                    setIsSubscribed(false);
                    setRoadmap(null);
                    break;
                default:
                    break;
            }
        });

        return () => unsubscribe();
    }, []);
    
    useEffect(() => {
        if (userId) {
            fetchProgress();
            fetchSubscriptionStatus();
        }
    }, [userId, fetchProgress, fetchSubscriptionStatus]);
    
    async function checkAuthState() {
        try {
            const user = await getCurrentUser();
            setIsAuthenticated(true);
            setUserId(user?.userId || user?.username || null);
        } catch {
            setIsAuthenticated(false);
            setUserId(null);
        }
    }

    async function updateProgress(newProgress) {
        setProgress(newProgress);
        try {
            const existing = (await DataStore.query(Progress)).filter(p => !p.id.includes('::project::'));
            if (existing.length > 0) {
                await DataStore.save(
                    Progress.copyOf(existing[0], updated => {
                        updated.data = JSON.stringify(newProgress);
                    })
                );
            } else {
                await DataStore.save(
                    new Progress({
                        data: JSON.stringify(newProgress)
                    })
                );
            }
        } catch (error) {
            console.error('Error saving progress:', error);
        }
    }

    async function updateSubscription() {
        setIsSubscribed(await checkLearningAccess('any'));
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const generateRoadmap = (newRoadmap) => {
        setRoadmap(newRoadmap);
    };

    return (
        <HelmetProvider>
            <Router>
                <CampaignAttribution />
                <div className="flex min-h-screen bg-slate-900">
                    <Sidebar isSubscribed={isSubscribed} />
                    <div className="flex-1 flex flex-col overflow-hidden">
                        <TopNavBar />
                        <Breadcrumbs />
                        <Routes>
                            <Route 
                                path="/dashboard" 
                                element={
                                    <>
                                        <SEO page="home" />
                                        <LandingPage />
                                    </>
                                }
                            />
                            <Route 
                                path="/roles" 
                                element={
                                    <>
                                        <SEO page="roles" />
                                        <RolesPage />
                                    </>
                                }
                            />
                            <Route
                              path="/portal"
                              element={
                                <>
                                  <SEO page="portal" />
                                  <PortalPage />
                                </>
                              }
                            />
                            <Route
                              path="/pricing"
                              element={
                                <>
                                  <SEO page="pricing" />
                                  <PricingPage />
                                </>
                              }
                            />
                            <Route
                              path="/profile"
                              element={
                                <>
                                  <SEO page="profile" />
                                  <UserProfile />
                                </>
                              }
                            />
                            <Route
                              path="/checkout-success"
                              element={
                                <>
                                  <SEO page="checkout" />
                                  <CheckoutSuccess />
                                </>
                              }
                            />
                            <Route
                                path="/devsecops" 
                                element={
                                    <>
                                        <SEO page="devsecops" />
                                        <Questionnaire 
                                            formData={formData}
                                            handleInputChange={handleInputChange}
                                            generateRoadmap={generateRoadmap}
                                            roadmap={roadmap}
                                            isSubscribed={isSubscribed}
                                            setIsSubscribed={updateSubscription}
                                            progress={progress}
                                            updateProgress={updateProgress}
                                        />
                                    </>
                                } 
                            />
                            <Route 
                                path="/devex" 
                                element={
                                    <>
                                        <SEO page="devex" />
                                        <DevExQuestionnaire 
                                            formData={formData}
                                            handleInputChange={handleInputChange}
                                            generateRoadmap={generateRoadmap}
                                            roadmap={roadmap}
                                            isSubscribed={isSubscribed}
                                            setIsSubscribed={updateSubscription}
                                            progress={progress}
                                            updateProgress={updateProgress}
                                        />
                                    </>
                                } 
                            />
                            <Route 
                                path="/cloud-platform-engineer" 
                                element={
                                    <>
                                        <SEO page="cloudPlatformEngineer" />
                                        <CloudPlatformEngineerQuestionnaire 
                                            formData={formData}
                                            handleInputChange={handleInputChange}
                                            generateRoadmap={generateRoadmap}
                                            roadmap={roadmap}
                                            isSubscribed={isSubscribed}
                                            setIsSubscribed={updateSubscription}
                                            progress={progress}
                                            updateProgress={updateProgress}
                                        />
                                    </>
                                } 
                            />
                            <Route 
                                path="/devops-questionnaire" 
                                element={
                                    <>
                                        <SEO page="devops" />
                                        <DevOpsQuestionnaire 
                                            formData={formData}
                                            handleInputChange={handleInputChange}
                                            generateRoadmap={generateRoadmap}
                                            roadmap={roadmap}
                                            isSubscribed={isSubscribed}
                                            setIsSubscribed={updateSubscription}
                                            progress={progress}
                                            updateProgress={updateProgress}
                                        />
                                    </>
                                } 
                            />
                            <Route 
                                path="/sre-questionnaire" 
                                element={
                                    <>
                                        <SEO page="sre" />
                                        <SREQuestionnaire 
                                            formData={formData}
                                            handleInputChange={handleInputChange}
                                            generateRoadmap={generateRoadmap}
                                            roadmap={roadmap}
                                            isSubscribed={isSubscribed}
                                            setIsSubscribed={updateSubscription}
                                            progress={progress}
                                            updateProgress={updateProgress}
                                        />
                                    </>
                                } 
                            />
                            <Route 
                                path="/penetration-testing" 
                                element={
                                    <>
                                        <SEO page="penetrationTesting" />
                                        <PenetrationTestingQuestionnaire 
                                            formData={formData}
                                            handleInputChange={handleInputChange}
                                            generateRoadmap={generateRoadmap}
                                            roadmap={roadmap}
                                            isSubscribed={isSubscribed}
                                            setIsSubscribed={updateSubscription}
                                            progress={progress}
                                            updateProgress={updateProgress}
                                        />
                                    </>
                                } 
                            />
                            <Route 
                                path="/cybergrc" 
                                element={
                                    <>
                                        <SEO page="cyberGRC" />
                                        <CyberGRCQuestionnaire 
                                            formData={formData}
                                            handleInputChange={handleInputChange}
                                            generateRoadmap={generateRoadmap}
                                            roadmap={roadmap}
                                            isSubscribed={isSubscribed}
                                            setIsSubscribed={updateSubscription}
                                            progress={progress}
                                            updateProgress={updateProgress}
                                        />
                                    </>
                                } 
                            />
                            <Route 
                                path="/product-manager" 
                                element={
                                    <>
                                        <SEO page="productManager" />
                                        <ProductManagerQuestionnaire 
                                            formData={formData}
                                            handleInputChange={handleInputChange}
                                            generateRoadmap={generateRoadmap}
                                            roadmap={roadmap}
                                            isSubscribed={isSubscribed}
                                            setIsSubscribed={updateSubscription}
                                            progress={progress}
                                            updateProgress={updateProgress}
                                        />
                                    </>
                                } 
                            />
                            <Route 
                                path="/ai-data-engineer" 
                                element={
                                    <>
                                        <SEO page="aiDataEngineer" />
                                        <AIDataEngineerQuestionnaire 
                                            formData={formData}
                                            handleInputChange={handleInputChange}
                                            generateRoadmap={generateRoadmap}
                                            roadmap={roadmap}
                                            isSubscribed={isSubscribed}
                                            setIsSubscribed={updateSubscription}
                                            progress={progress}
                                            updateProgress={updateProgress}
                                        />
                                    </>
                                } 
                            />
                            <Route 
                                path="/security-analyst" 
                                element={
                                    <>
                                        <SEO page="securityAnalyst" />
                                        <SecurityAnalystQuestionnaire 
                                            formData={formData}
                                            handleInputChange={handleInputChange}
                                            generateRoadmap={generateRoadmap}
                                            roadmap={roadmap}
                                            isSubscribed={isSubscribed}
                                            setIsSubscribed={updateSubscription}
                                            progress={progress}
                                            updateProgress={updateProgress}
                                        />
                                    </>
                                } 
                            />
                            <Route 
                                path="/full-stack-engineer" 
                                element={
                                    <>
                                        <SEO page="fullStackEngineer" />
                                        <FullStackEngineerQuestionnaire 
                                            formData={formData}
                                            handleInputChange={handleInputChange}
                                            generateRoadmap={generateRoadmap}
                                            roadmap={roadmap}
                                            isSubscribed={isSubscribed}
                                            setIsSubscribed={updateSubscription}
                                            progress={progress}
                                            updateProgress={updateProgress}
                                        />
                                    </>
                                } 
                            />
                            <Route 
    path="/devsecai" 
    element={
        <>
            <SEO page="devsecai" />
            <DevSecAIQuestionnaire 
                formData={formData}
                handleInputChange={handleInputChange}
                generateRoadmap={generateRoadmap}
                roadmap={roadmap}
                isSubscribed={isSubscribed}
                setIsSubscribed={updateSubscription}
                progress={progress}
                updateProgress={updateProgress}
            />
        </>
    } 
/>
                            {getFeatureFlag('showProgressTracker') && (
                            <Route 
                                path="/progress" 
                                element={
                                    <>
                                        <SEO page="progress" />
                                        <ProgressTracker 
                                            roadmap={roadmap || defaultRoadmap}
                                            progress={progress}
                                            updateProgress={updateProgress}
                                            isSubscribed={isSubscribed}
                                            setIsSubscribed={updateSubscription}
                                        />
                                    </>
                                } 
                            />
                            )}
                            <Route 
                                path="/terms-of-service" 
                                element={
                                    <>
                                        <SEO page="terms" />
                                        <TermsOfService />
                                    </>
                                } 
                            />
                            <Route 
                                path="/privacy-policy" 
                                element={
                                    <>
                                        <SEO page="privacy" />
                                        <PrivacyPolicy />
                                    </>
                                } 
                            />
                            <Route path="/terms" element={<Navigate to="/terms-of-service" replace />} />
                            <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
                            <Route path="/" element={<Navigate to="/dashboard" replace />} />
                            <Route path="*" element={<NotFound />} />
                        </Routes>
                    </div>
                </div>
            
                <CookieConsent />
            </Router>
        </HelmetProvider>
    );
}

export default App;
